import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import MuiLink from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import { supabaseAuthService } from '../services/supabaseAuthService';
import { supabase } from '../config/supabase';
import { keyframes } from '@mui/system';
import Grid from '@mui/material/Grid';
import { safeLocalStorage } from '../utils/storageUtils';
import { getSafeRedirectRoute } from '../utils/routeUtils';
import { adminNavItems, patientNavItems, physicianNavItems, sponsorNavItems } from '../navigation/navItems';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Login = () => {
  const navigate = useNavigate();
  const { isAuthenticated, profile, isAdmin, isProvider } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '', clinicId: '' });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [loadingClinics, setLoadingClinics] = useState(true);

  const validateForm = () => {
    let valid = true;
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      valid = false;
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
      valid = false;
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  // Fetch available clinics/tenants
  useEffect(() => {
    const fetchClinics = async () => {
      try {
        const { data, error } = await supabase
          .from('tenants')
          .select('id, name, subdomain')
          .order('name');

        if (error) {
          // If table doesn't exist or no access, continue without clinic selector
          console.warn('Unable to fetch clinics:', error);
          setClinics([]);
          setLoadingClinics(false);
          return;
        }

        setClinics(data || []);

        // Auto-select if only one clinic
        if (data && data.length === 1) {
          setFormData(prev => ({ ...prev, clinicId: data[0].id }));
        }
      } catch (error) {
        console.error('Error fetching clinics:', error);
        // Don't show error - just hide clinic selector
        setClinics([]);
      } finally {
        setLoadingClinics(false);
      }
    };

    fetchClinics();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  // Get navigation items based on user role
  // Prioritize physician view for providers who are also admins
  const getNavItems = () => {
    if (isProvider) return physicianNavItems;
    if (isAdmin) return adminNavItems;
    if (profile?.tenant_role === 'sponsor') return sponsorNavItems;
    return patientNavItems;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);
      try {
        const { data, error } = await supabaseAuthService.signIn(
          formData.email,
          formData.password
        );

        if (error) {
          throw error;
        }

        if (data?.user) {
          // Fetch user profile to check approval status
          const userProfile = await supabaseAuthService.getUserProfile(data.user.id);

          // Check approval status for physicians and sponsors
          if (userProfile && (userProfile.tenant_role === 'physician' || userProfile.tenant_role === 'sponsor')) {
            if (userProfile.approval_status === 'pending') {
              // Redirect to pending approval page
              navigate('/pending-approval', {
                replace: true,
                state: {
                  approvalStatus: 'pending',
                  role: userProfile.tenant_role,
                  message: 'Your application is under review. You will be notified once approved.'
                }
              });
              return;
            } else if (userProfile.approval_status === 'rejected') {
              // Show rejection message
              setErrors({
                ...errors,
                submit: 'Your application was rejected. Please contact support for more information.'
              });
              return;
            }
          }

          // Successfully logged in and approved - wait a moment for AuthContext to update
          // Then navigate based on user role
          setTimeout(() => {
            // Determine user role from the profile we just fetched
            const userRole = userProfile?.tenant_role || profile?.tenant_role;
            const platformRole = userProfile?.platform_role || profile?.platform_role;
            const isUserProvider = userRole === 'physician' || platformRole === 'super_admin' || isProvider;
            const isUserAdmin = platformRole === 'super_admin' || platformRole === 'admin' || isAdmin;
            const isUserSponsor = userRole === 'sponsor';

            // Get navigation items based on the actual user profile (not just context)
            let navItems;
            if (isUserProvider) {
              navItems = physicianNavItems;
            } else if (isUserAdmin) {
              navItems = adminNavItems;
            } else if (isUserSponsor) {
              navItems = sponsorNavItems;
            } else {
              navItems = patientNavItems;
            }

            // Check for last visited page
            const lastPage = safeLocalStorage.getItem('lastVisitedPage');

            // Get safe redirect route (validates route exists and user has access)
            const safeRoute = getSafeRedirectRoute(
              lastPage,
              navItems,
              userProfile || profile,
              { 
                isAdmin: isUserAdmin, 
                isProvider: isUserProvider, 
                isSponsor: isUserSponsor 
              }
            );

            // Save last visited page for persistence
            safeLocalStorage.setItem('lastVisitedPage', safeRoute);

            navigate(safeRoute, { replace: true });
          }, 500);
        }
      } catch (error) {
        console.error('Error logging in:', error);
        let errorMessage = 'Invalid email or password';

        if (error.message?.includes('Invalid login credentials')) {
          errorMessage = 'Invalid email or password. Please try again.';
        } else if (error.message?.includes('Email not confirmed')) {
          errorMessage = 'Please check your email and confirm your account.';
        } else if (error.message?.includes('Too many requests')) {
          errorMessage = 'Too many failed attempts. Please try again later.';
        } else if (error.message) {
          errorMessage = error.message;
        }

        setErrors({ ...errors, submit: errorMessage });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Auto-redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && profile) {
      // Get navigation items for route validation
      const navItems = getNavItems();

      // Check for last visited page
      const lastPage = safeLocalStorage.getItem('lastVisitedPage');

      // Get safe redirect route (validates route exists and user has access)
      const safeRoute = getSafeRedirectRoute(
        lastPage,
        navItems,
        profile,
        { isAdmin, isProvider, isSponsor: profile?.tenant_role === 'sponsor' }
      );

      navigate(safeRoute, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, profile, isAdmin, isProvider, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="xs" sx={{ px: { xs: 1, sm: 2 }, width: '100%', maxWidth: '100%' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontWeight: 400, fontSize: { xs: '1rem', sm: '1.1rem' } }}
          >
            Sign in to access your telemedicine dashboard
          </Typography>
        </Box>

        <Box sx={{ maxWidth: 500, mx: 'auto' }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              width: '100%',
              maxWidth: 400,
              mx: 'auto',
              overflowX: 'auto',
              borderRadius: 2,
              animation: `${fadeIn} 0.5s ease-out`,
              '&:hover': {
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
              },
              transition: 'box-shadow 0.3s ease-in-out',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography
                component={Link}
                to="/"
                variant="h4"
                sx={{
                  mb: 2,
                  color: 'info.main',
                  fontWeight: 600,
                  letterSpacing: '-0.5px',
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                  }
                }}
              >
                Sign In to Alpha01
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, textAlign: 'center' }}
              >
                Multi-Tenant Telemedicine Platform
              </Typography>

              {errors.submit && (
                <Alert
                  severity="error"
                  sx={{
                    width: '100%',
                    mb: 2,
                    animation: `${fadeIn} 0.3s ease-out`,
                  }}
                >
                  {errors.submit}
                </Alert>
              )}

              <Box component="form" sx={{ width: '100%' }} onSubmit={handleSubmit}>
                {/* Clinic/Tenant Selector - Only show if clinics are available */}
                {(clinics.length > 0 || loadingClinics) && (
                  <FormControl
                    fullWidth
                    margin="normal"
                    disabled={loadingClinics || clinics.length === 0}
                    error={!!errors.clinic}
                  >
                    <InputLabel id="clinic-select-label">
                      {loadingClinics ? 'Loading clinics...' : 'Select Clinic / Organization'}
                    </InputLabel>
                    <Select
                      labelId="clinic-select-label"
                      id="clinic-select"
                      name="clinicId"
                      value={formData.clinicId}
                      label="Select Clinic / Organization"
                      onChange={handleChange}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: errors.clinic ? 'error.main' : undefined,
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>-- Select your clinic --</em>
                      </MenuItem>
                      {clinics.map((clinic) => (
                        <MenuItem key={clinic.id} value={clinic.id}>
                          {clinic.name}
                          {clinic.subdomain && (
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              ({clinic.subdomain})
                            </Typography>
                          )}
                        </MenuItem>
                      ))}
                      <Divider />
                      <MenuItem value="platform" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                        🏢 Platform Admin (Alpha01 HQ)
                      </MenuItem>
                    </Select>
                    {errors.clinic && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.clinic}
                      </Typography>
                    )}
                  </FormControl>
                )}

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={isLoading}
                  sx={{
                    mt: 3,
                    mb: 2,
                    py: 1.5,
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                    },
                    transition: 'transform 0.2s ease-in-out',
                    minHeight: 48,
                  }}
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>

                <Box sx={{ textAlign: 'right', mb: 2 }}>
                  <MuiLink
                    component={Link}
                    to="/forgot-password"
                    variant="body2"
                    color="info.main"
                    sx={{
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Forgot password?
                  </MuiLink>
                </Box>

                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    OR
                  </Typography>
                </Divider>

                <Box sx={{ textAlign: 'center' }}>
                  <MuiLink
                    component={Link}
                    to="/register"
                    variant="body1"
                    color="info.main"
                    sx={{
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Don't have an account? Sign Up
                  </MuiLink>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
